<template>
	<BasePlaceHolder :placehold="loading" :section-count="2" :class="{ 'container py-400': loading }">
		<section :class="[backgroundColor, foregroundColor, 'marketing-section']">
			<div class="container">
				<div class="col-10-xl mx-auto">
					<h2 v-if="featuredFundOverline" :class="[foregroundColorSecondary, 'overline mb-100']">
						{{ featuredFundOverline }}
					</h2>
					<div class="display-flex-md justify-content-space-between">
						<article class="col-7-md mb-150-smo">
							<h3 v-if="featuredFundHeading" class="heading-1 mb-100">
								{{ featuredFundHeading }}
							</h3>
							<RichTextComponent
								v-if="featuredFundSummary"
								:blocks="featuredFundSummary"
								:is-dark-mode="true"
							/>
							<BaseCtaLink
								v-if="featuredFundCtaLink"
								classes="mt-50"
								:link="featuredFundCtaLink"
								:is-orange="theme === 'dark'"
								icon="arrow"
							/>
						</article>
						<div class="col-5-md text-align-center my-150-smo">
							<div v-for="stat in importantStats" :key="stat.label" class="mb-200">
								<strong class="heading-1">{{ stat.value }}</strong>
								<p :class="[statLabelColor, 'mt-50']">
									<BaseTooltip v-if="stat.tooltip">
										<template #trigger
											><span>{{ stat.label }}</span>
										</template>
										<span>{{ stat.tooltip }}</span>
									</BaseTooltip>
									<span v-else>{{ stat.label }}</span>
								</p>
							</div>
						</div>
					</div>

					<div v-if="showSectors" class="my-400-md mb-200-smo">
						<p :class="[foregroundColorSecondary, 'overline mb-150']"> Sectors </p>
						<VentureCapitalSectorsScroll />
					</div>

					<div
						v-if="props.showFeaturedAsset"
						class="asset-holding__container module-radius-lg border-color-reversed p-300-md p-150-smo bg-color-content-primary"
					>
						<div class="asset-holding__company-container">
							<div class="asset-holding__grid-wrapper position-relative">
								<img
									:src="artificialIntelligenceCompany"
									class="asset-holding__grid-wrapper__blurred"
									alt=""
									role="presentation"
								/>
								<img
									:src="propTechCompany"
									class="asset-holding__grid-wrapper__blurred"
									alt=""
									role="presentation"
								/>
								<CompanyElementCard
									v-if="asset"
									class="asset-holding__grid-wrapper__available position-absolute"
									:asset="asset"
								/>
							</div>
						</div>
						<!-- https://fundrise.atlassian.net/browse/WWW-3824 -->
						<div v-if="showFeaturedInvestment">
							<p class="overline text-color-gray-20"> Featured investment </p>
							<h3
								:class="[
									'text-color-white font-weight-lighter line-height-125-smo mt-100',
									appStore.isMobile ? 'heading-1' : 'heading-2'
								]"
							>
								Canva
							</h3>
							<p class="mb-100 mt-50-smo">
								Canva is the leading online design and visual communication platform. Their
								collaborative and intuitive software makes it easy for anyone (even non-designers) to
								create professional designs. Founded in 2012 in Australia, Canva has seen explosive
								growth since its inception, reaching 10 million users by 2015 and 135 million users by
								2023. The company is emerging as the leading challenger of Adobe, the dominant incumbent
								player in design software.
							</p>
							<BaseCtaLink :is-orange="true" :link="canvaLink" icon="arrow" />
						</div>
					</div>
					<div
						v-if="props.disclaimer || importantStatsDisclaimer"
						:class="[
							'display-flex',
							{ 'justify-content-space-around': !props.disclaimer && importantStatsDisclaimer }
						]"
					>
						<p
							:class="[
								'muted body-xs mt-400-md mt-200-smo',
								{
									'col-7-md': props.disclaimer
								}
							]"
						>
							{{ props.disclaimer || importantStatsDisclaimer }}
						</p>
					</div>
				</div>
			</div>
		</section>
	</BasePlaceHolder>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue';
import { appCloudfrontPath } from '@constants';
import { Asset } from 'types/assets';
import { canvaCompanyId } from '@constants/reit';
import CompanyElementCard from '@components/company-element/company-element-card.vue';
import { getAsset } from '@api/sanity/proxy/assets';
import { getVentureMarketingPageFeaturedFund } from '@api/sanity/graphql/venture-marketing-page';
import { Link } from 'types/layout';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityMarketingAboutFund } from 'types/sanity/marketing-components';
import { SanityRichTextBlock } from 'types/sanity';
import { useAppStore } from '@stores/app';
import VentureCapitalSectorsScroll from '@components/marketing-site/venture-capital/venture-capital-sectors-scroll.vue';
import { VentureImportantStat } from 'types/sanity/venture-marketing';

interface Props {
	theme?: 'dark' | 'light';
	showSectors?: boolean;
	showFeaturedAsset?: boolean;
	stats?: Array<VentureImportantStat> | null;
	disclaimer?: string | null;
}
const props = withDefaults(defineProps<Props>(), {
	theme: 'dark',
	showSectors: true,
	showFeaturedAsset: false,
	stats: null,
	disclaimer: null
});

const appStore = useAppStore();

const showFeaturedInvestment = false;
const canvaLink: Link = {
	router: 'blog-post',
	params: { blogPostUrlSlug: 'investment-in-canva' },
	target: '_blank',
	text: 'Read more about this investment'
};
const loading = ref(true);
const artificialIntelligenceCompany = `${appCloudfrontPath}/images/offerings/growth-tech/gto-ai-holding.png`;

const propTechCompany = `${appCloudfrontPath}/images/offerings/growth-tech/gto-prop-tech-holding.png`;

const asset = ref<Asset | null>(null);

const sanityFeaturedFundData: Ref<SanityMarketingAboutFund | null> = ref(null);

const featuredFundOverline = computed((): string => sanityFeaturedFundData.value?.featuredOverline ?? '');
const featuredFundHeading = computed((): string => sanityFeaturedFundData.value?.featuredHeading ?? '');
const featuredFundSummary: Ref<Array<SanityRichTextBlock>> = ref([]);
const featuredFundCtaLink: Ref<Link | null> = ref(null);

const importantStats = computed(
	(): Array<VentureImportantStat> => props.stats ?? sanityFeaturedFundData.value?.featuredPanelStats ?? []
);
const importantStatsDisclaimer = computed((): string => sanityFeaturedFundData.value?.featuredDisclaimer ?? '');

const backgroundColor = computed(() => (props.theme === 'dark' ? 'bg-color-content-primary' : 'bg-color-bg-tertiary'));

const foregroundColor = computed(() => (props.theme === 'dark' ? 'text-color-white' : 'text-color-content-primary'));
const foregroundColorSecondary = computed(() =>
	props.theme === 'dark' ? 'text-color-gray-20' : 'text-color-content-tertiary'
);

const statLabelColor = computed(() => (props.theme === 'dark' ? 'text-color-gray-40' : 'text-color-content-tertiary'));

async function setup(): Promise<void> {
	try {
		if (props.showFeaturedAsset) {
			asset.value = await getAsset(canvaCompanyId, { elementType: 'VENTURE' });
		}
		const sanityResponse = await getVentureMarketingPageFeaturedFund();

		sanityFeaturedFundData.value = sanityResponse;

		featuredFundSummary.value = sanityResponse?.featuredSummaryRaw ?? [];
		featuredFundCtaLink.value = sanityResponse?.featuredFundCta
			? {
					href: sanityResponse?.featuredFundCta?.url ?? '',
					text: sanityResponse?.featuredFundCta?.linkText ?? '',
					target: sanityResponse?.featuredFundCta?.openInNewTab ? '_blank' : '_self'
				}
			: null;
	} catch {
	} finally {
		loading.value = false;
	}
}
setup();
</script>
<style lang="scss" scoped>
@use '../../../styles/utilities/respond-to.scss' as *;

// Supports sector cards in horizontal scroll extending to viewport edge.
:deep(.horizontal) {
	@include respond-to(lg) {
		width: calc((100% + (100vw - 100%) / 2) - $md-viewport-padding);
		max-width: revert;
		margin-right: calc(50% - 50vw);
	}
}

$small-screen-size-break: 568px;
$medium-screen-size-break: 981px;
.asset-holding {
	&__container {
		display: flex;
		flex-direction: column-reverse;
		@media screen and (min-width: $medium-screen-size-break) {
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			column-gap: 4.5rem;
		}
		@media screen and (max-width: $small-screen-size-break - 1) {
			padding-bottom: 6.5rem;
		}
		@media screen and (min-width: $small-screen-size-break - 1) and (max-width: $medium-screen-size-break) {
			padding-bottom: 4.5rem;
		}
	}
	&__company-container {
		display: flex;
		grid-row: 1;
		justify-content: center;
		@media screen and (min-width: $medium-screen-size-break) {
			align-self: center;
			grid-row: unset;
			justify-content: unset;
		}
		@media screen and (max-width: $small-screen-size-break - 1) {
			margin-top: 7rem;
		}
		@media screen and (min-width: $small-screen-size-break) and (max-width: $medium-screen-size-break) {
			margin-top: 5rem;
		}
	}
	&__grid-wrapper {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		&__blurred {
			filter: blur(3px);
			-webkit-filter: blur(3px);
			opacity: 0.1;
			max-width: none;
			width: 160px;
			height: 170px;
			@media screen and (min-width: $small-screen-size-break) {
				width: 260px;
				height: 232px;
			}
		}
		&__available {
			height: 310px;
			width: 271px;
			top: -40%;
			left: 7.5%;
			@media screen and (min-width: $small-screen-size-break) {
				height: 308px;
				width: 336px;
				left: 18%;
				top: -16%;
			}
		}
	}
}
</style>
